import { action, computed, makeObservable, observable } from 'mobx';
import uniq from 'lodash/uniq';

import { Region } from '@components/common/constants';
import { getApiErrorMessage } from '@components/common/util/util';
import { apiAuthCaller } from '@services/apiCaller';
import { IRootStore } from '@stores/index';
import { WorkspaceEnvironment } from '@stores/workspace';
import { WorkspaceStatus } from '@stores/workspace/constants';
import {
  ApiEventUsageStatus,
  getEventUsageStatus,
  getOrgLockedResources,
  getOrgPlanDetails,
  BillingPlan,
  LockedResource,
} from '@api';

import { PlanNameType } from './billing';
import { LegacyInviteRole, Member, MemberStore, ServerStatus, UserRoles } from './member';
import { experiment, FeatureFlagKeys } from '@lib/experiment';

type Seed = {
  id: string;
  name: string;
  planName: PlanNameType;
  grafanaURL?: string;
  ownerId: string;
  enforcedMfa?: boolean;
  userRoles: {
    user: { id: string; email: string; name: string };
    roles: UserRoles;
  }[];
  invitedUsers: {
    id: string;
    name: string;
    email: string;
    status: ServerStatus;
    role: LegacyInviteRole;
    userRoles: UserRoles;
    createdAt: string;
  }[];
  workspaceId: string;
};

export interface Workspace {
  id: string;
  name: string;
  environment: WorkspaceEnvironment;
  status: WorkspaceStatus;
  defaultRegion: Region;
}

export interface IOrganization {
  id: string;
  name: string;
  isLoading: boolean;
  planName?: PlanNameType;
  grafanaURL?: string;
  ownerId: string;
  members: Member[];
  admins: Member[];
  updateEnforcedMfa: (val: boolean) => void;
  updateName: (val: string) => Promise<boolean>;
  seed: (obj: Seed) => void;
  reset: () => void;
  setMemberStatus: (email: string) => void;
  workspaces: Workspace[];
  accessibleWorkspaces: Workspace[];
  workspaceRegions: Region[];
  disabledWorkspaces: Workspace[];
  activeWorkspaces: Workspace[];
  enforcedMfa?: boolean;
  eventUsageStatus: ApiEventUsageStatus | null;
  eventUsageStatusLoading: boolean;
  loadEventUsageStatus: () => Promise<void>;
  loadOrgPlan: () => Promise<void>;
  eventsUsageCount: number;
  eventsUsageLimit: number;
  isSelfServe: boolean;
  orgPlan: BillingPlan | null;
  lockedResources: LockedResource[];
  loadLockedResources: () => Promise<void>;
}

export class OrganizationStore implements IOrganization {
  private rootStore: IRootStore;

  @observable public id = '';

  @observable public name = '';

  @observable public planName?: PlanNameType = undefined;

  @observable public ownerId = '';

  @observable public isLoading = true;

  @observable public grafanaURL: string | undefined = '';

  @observable public members: MemberStore[] = [];

  @observable public enforcedMfa?: boolean = undefined;

  @observable public orgPlan: BillingPlan | null = null;

  @observable public eventUsageStatus: ApiEventUsageStatus | null = null;

  @observable public eventUsageStatusLoading = false;

  @observable public lockedResources: LockedResource[] = [];

  constructor(rootStore: IRootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
  }

  @action.bound
  async loadLockedResources() {
    const { id, isSelfServe, rootStore } = this;

    if (
      !id ||
      !isSelfServe ||
      !experiment.isFeatureEnabled(FeatureFlagKeys.enablePlgLockedResources)
    ) {
      return;
    }

    try {
      const { data } = await getOrgLockedResources(id);
      this.lockedResources = data.data;
    } catch (e) {
      rootStore.messagesStore.showErrorMessage((e as Error).message);
    }
  }

  @action.bound
  seed(data: Seed) {
    this.id = data.id;
    this.name = data.name;
    this.planName = data.planName;
    this.grafanaURL = data.grafanaURL;
    this.ownerId = data.ownerId;
    this.enforcedMfa = data.enforcedMfa;
    this.populateMembers(data);
    this.isLoading = false;
    this.eventUsageStatus = null;
  }

  private populateMembers({ userRoles, invitedUsers, workspaceId }: Seed) {
    this.members = userRoles.map(
      (userRole) =>
        new MemberStore(
          {
            id: userRole.user.id,
            name: userRole.user.name,
            email: userRole.user.email,
            roles: userRole.roles,
            status: undefined,
            invitedAt: '',
            workspaceId,
          },
          this.rootStore,
        ),
    );

    this.members = this.members.concat(
      invitedUsers.map(
        (user) =>
          new MemberStore(
            {
              id: user.id,
              name: user.name,
              email: user.email,
              status: user.status,
              roles: user.userRoles,
              invitedAt: user.createdAt,
              workspaceId,
            },
            this.rootStore,
          ),
      ),
    );
  }

  @action.bound
  async updateName(name: string) {
    const {
      messagesStore: { showErrorMessage },
    } = this.rootStore;
    try {
      await apiAuthCaller().post(`/updateOrgName`, { name });
      this.name = name;
      return true;
    } catch (error) {
      showErrorMessage(getApiErrorMessage(error, 'Failed to update organization name'));
    }
    return false;
  }

  @action.bound
  setMemberStatus(email: string) {
    const member = this.members.find((user) => user.email === email);
    member!.status = 'pending';
  }

  @action.bound
  async updateEnforcedMfa(enforcedMfa: boolean) {
    const {
      userStore: { updateEnforcedMfa },
      messagesStore,
    } = this.rootStore;
    try {
      await apiAuthCaller().post(`/enforceMfa`, { enforcedMfa });
      this.enforcedMfa = enforcedMfa;
      await updateEnforcedMfa();
      messagesStore.showSuccessMessage('Successfully updated enforced MFA option');
    } catch (error) {
      messagesStore.showErrorMessage('Failed to update enforced MFA option');
    }
  }

  @action.bound
  async loadOrgPlan() {
    if (
      !this.id ||
      !experiment.isFeatureEnabled(FeatureFlagKeys.enablePlgTrial) ||
      !this.rootStore.featuresStore.has('BILLING')
    ) {
      return;
    }
    const response = await getOrgPlanDetails(this.id);
    this.orgPlan = response.data;
  }

  reset() {
    this.id = '';
    this.name = '';
    this.grafanaURL = '';
    this.ownerId = '';
    this.members = [];
    this.isLoading = true;
    this.orgPlan = null;
  }

  @action.bound
  async loadEventUsageStatus() {
    if (!this.isSelfServe) {
      this.eventUsageStatus = null;
      return;
    }

    this.eventUsageStatusLoading = true;

    try {
      const response = await getEventUsageStatus(this.id);
      this.eventUsageStatus = response.data.data;
    } catch (e) {
      this.eventUsageStatus = null;
    } finally {
      this.eventUsageStatusLoading = false;
    }
  }

  @computed get admins() {
    return this.members.filter((member) => member.isOrgAdmin);
  }

  @computed get isSelfServe() {
    return this.rootStore.featuresStore.has('BILLING');
  }

  @computed get workspaces() {
    const {
      userStore: { workspaces, selectedWorkspaceId },
    } = this.rootStore;
    return workspaces
      .filter((ws) => ws.organizationId === this.id)
      .sort((a, b) => Number(b.id === selectedWorkspaceId) - Number(a.id === selectedWorkspaceId));
  }

  @computed get accessibleWorkspaces() {
    const {
      userStore: { accessibleWorkspaces, selectedWorkspaceId },
      featuresStore,
    } = this.rootStore;
    if (!featuresStore.has('NEW_ROLES_FLOW')) {
      return this.workspaces;
    }

    return accessibleWorkspaces
      .filter((ws) => ws.organizationId === this.id)
      .sort((a, b) => Number(b.id === selectedWorkspaceId) - Number(a.id === selectedWorkspaceId));
  }

  @computed get workspaceRegions() {
    // is this relevant to the data-residency feature?
    return uniq(this.workspaces.map((w) => w.defaultRegion));
  }

  @computed get disabledWorkspaces() {
    return this.workspaces.filter((ws) => ws.status === WorkspaceStatus.DISABLED);
  }

  @computed get activeWorkspaces() {
    return this.workspaces.filter((ws) => ws.status === WorkspaceStatus.ACTIVE);
  }

  @computed get eventsUsageCount() {
    return this.eventUsageStatus?.usage || 0;
  }

  @computed get eventsUsageLimit() {
    const { billingStore, featuresStore, trialStore } = this.rootStore;
    const { tenantInfo, currentPlan } = billingStore;

    if (this.isSelfServe) {
      const limit = featuresStore.getFeatureConfiguration('EVENT_STREAM_LIMIT') as number;
      const trialLimit = trialStore.isOnTrial
        ? (trialStore.trialTargetPlan?.features?.EVENT_STREAM_LIMIT as number) || limit
        : null;
      const currentPlanLimit = currentPlan?.eventVolume;
      return trialLimit || currentPlanLimit || limit || 0;
    }

    const million = 1_000_000;
    return (tenantInfo?.contractEventVolume || 0) * million;
  }
}
